import React, { useState, useEffect, ChangeEvent, DragEvent } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TabContext, TabPanel } from '@mui/lab';
import { CloudUpload, Save, GetApp } from '@mui/icons-material';
import { ChapterIndicatorThemeDto, IndicatorDto, OrganizationIndicatorData } from '../../../../types/indicator';
import ShvkTextField from '../../../../styled/ShvkTextField';
import ShvkButton from '../../../../styled/ShvkButton';
import useStore, { rootModule } from '../../../../store/storeContext';
import { OrganizationDto } from '../../../../types/organization';

const PREFIX = 'UploadCustomIndicatorDialog';

const classes = {
    tableCell: `${PREFIX}-tableCell`,
    warningText: `${PREFIX}-warningText`,
    warningCell: `${PREFIX}-warningCell`,
};

const { theming } = rootModule;

const StyledDialog = styled(Dialog)(({ theme }) => ({
    [`& .${classes.tableCell}`]: {
        border: '1px solid' + theming.shvkLightGrey,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },

    [`& .${classes.warningText}`]: {
        padding: theme.spacing(1),
        color: theme.palette.error.main,
    },

    [`& .${classes.warningCell}`]: {
        backgroundColor: theming.shvkRed10,
    },
}));

interface Props {
    isOpen: boolean;
    close(): void;
    indicatorPackageTheme?: ChapterIndicatorThemeDto | null;
}

interface State {
    activeTab: string;
}

function UploadCustomIndicatorDialog(props: Props) {
    const context = useStore();
    const { document, organization, localization, loadingIndicator, snackbar } = context;

    const [uploadedIndicators, setUploadedIndicators] = useState<IndicatorDto[]>([]);
    const [state, setState] = useState<State>({
        activeTab: 'upload',
    });

    useEffect(() => {
        if (props.isOpen) {
            setState((state) => ({ ...state, activeTab: 'upload' }));
            setUploadedIndicators([]);
        }
        return () => {
            setState((state) => ({ ...state, activeTab: 'upload' }));
            setUploadedIndicators([]);
        };
    }, [props.isOpen]);

    async function upload(file: File): Promise<void> {
        try {
            loadingIndicator.show();
            const indicator = await document.uploadIndicator(file);
            const indicatorAdded = [...uploadedIndicators, ...indicator];
            setUploadedIndicators(indicatorAdded);
            setState((state) => ({
                ...state,
                activeTab: indicatorAdded.length > 0 ? (indicatorAdded.length - 1).toString() : 'upload',
            }));
            snackbar.showSuccess('UPLOAD_SUCCESSFUL');
        } catch (error) {
            snackbar.showError(
                error.data['TranslationKey'] ? error.data['TranslationKey'] : 'ADD_CUSTOM_INDICATOR_ERROR',
            );
        } finally {
            loadingIndicator.hide();
        }
    }

    async function save(): Promise<void> {
        try {
            loadingIndicator.show();

            for (const indicator of uploadedIndicators) {
                const newIndicator: IndicatorDto = {
                    sourceCode: 'CUSTOM',
                    sourceName: '',
                    textOnly: false,
                    id: null,
                    alarmMax: null,
                    alarmMin: null,
                    targetLimit: null,
                    code: null,
                    direction: null,
                    indicatorSourceId: null,
                    infoUrl: null,
                    name: indicator.name,
                    orderNumber: null,
                    themeId: props.indicatorPackageTheme?.id || null,
                    type: null,
                    description: null,
                    organization: organization.currentOrganization,
                    data: indicator.data,
                    metadata: null,
                    sotkanetId: null,
                    origoOrganization: null,
                    enabled: null,
                    removable: null,
                    printable: null,
                    shortTermChangeData: null,
                    indicatorTopTitle: null,
                };

                await context.indicator.create(newIndicator);
            }
            snackbar.showSuccess();
        } catch (error) {
            snackbar.showError(error.data?.code);
        } finally {
            props.close();
            loadingIndicator.hide();
        }
    }

    async function downloadCustomIndicatorTemplate(): Promise<void> {
        loadingIndicator.show();
        try {
            await document.downloadExcelTemplate();
        } catch (error) {
            snackbar.showError(error.data?.code);
        } finally {
            props.close();
            loadingIndicator.hide();
        }
    }

    const handleIndicatorNameChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        indicator: IndicatorDto,
    ) => {
        const updatedIndicators = uploadedIndicators.map((uploadedIndicator) => {
            if (uploadedIndicator.name === indicator.name) {
                return { ...uploadedIndicator, name: e.target.value };
            }
            return uploadedIndicator;
        });
        setUploadedIndicators(updatedIndicators);
    };

    const handleDataChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        indicator: IndicatorDto,
        orgIndicatorData: OrganizationIndicatorData,
        year: number,
    ) => {
        const updatedIndicators = uploadedIndicators.map((uploadedIndicator) => {
            if (uploadedIndicator.name === indicator.name) {
                return {
                    ...uploadedIndicator,
                    data: uploadedIndicator.data.map((dataVariable) => {
                        if (
                            dataVariable.organization.id === orgIndicatorData.organization.id &&
                            dataVariable.year === year
                        ) {
                            return { ...dataVariable, value: parseInt(e.target.value) };
                        }
                        return dataVariable;
                    }),
                };
            }
            return uploadedIndicator;
        });
        setUploadedIndicators(updatedIndicators);
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
        if (!event?.target?.files) return;
        const file = event.target.files[0];
        if (!file) return;
        void upload(file);
    };

    const handleFileDrop = (event: DragEvent): void => {
        event.preventDefault();
        if (!event.dataTransfer?.files) return;
        const file = event.dataTransfer?.files[0];
        if (!file) return;
        void upload(file);
    };

    const handleTabChange = (_event: React.ChangeEvent<unknown>, newIndex: string): void => {
        setState((state) => ({ ...state, activeTab: newIndex }));
    };

    const isIndicatorOrganizationInCompareSettings = (indicatorOrganization: OrganizationDto): boolean => {
        return (
            comparisonSettings?.organizations.some((org) => {
                return org.id === indicatorOrganization.id;
            }) || false
        );
    };

    const { translate } = localization;
    const { comparisonSettings } = context.indicator;

    return (
        <StyledDialog
            open={props.isOpen}
            onClose={props.close}
            aria-labelledby="upload-custom-indicator-dialog"
            maxWidth="md"
            fullWidth
        >
            <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                <DialogTitle id="upload-custom-indicator-title">
                    {translate('ORGANIZATION_INDICATOR_UPLOAD') + ' - ' + props.indicatorPackageTheme?.name}
                </DialogTitle>
                <Button
                    component="span"
                    startIcon={<GetApp />}
                    style={{
                        alignSelf: 'flex-start',
                        marginTop: theming.spacing(2),
                        marginRight: theming.spacing(3),
                        color: '#FFF',
                        backgroundColor: theming.palette.secondary.main,
                    }}
                    variant="contained"
                    onClick={(): Promise<void> => downloadCustomIndicatorTemplate()}
                >
                    {translate('DOWNLOAD_EXCEL_TEMPLATE')}
                </Button>
            </Box>
            <DialogContent>
                <Tabs
                    value={state.activeTab}
                    indicatorColor="primary"
                    textColor="primary"
                    onChange={handleTabChange}
                    variant="fullWidth"
                >
                    <Tab label={translate('ORGANIZATION_INDICATOR_UPLOAD')} value="upload" />
                    {uploadedIndicators.map((indicator, i) => (
                        <Tab key={i} label={indicator.name} value={i.toString()} />
                    ))}
                </Tabs>
                <TabContext value={state.activeTab}>
                    <TabPanel value={'upload'}>
                        <input
                            accept=".xlsx"
                            style={{ display: 'none' }}
                            id="upload-custom-indicator-file-input"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <Paper
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={handleFileDrop}
                            style={{
                                backgroundColor: theming.shvkPetrol10,
                                paddingLeft: theming.spacing(4),
                                paddingRight: theming.spacing(4),
                                paddingTop: theming.spacing(8),
                                paddingBottom: theming.spacing(8),
                            }}
                        >
                            <Typography align="center">{translate('ADD_CUSTOM_INDICATOR_TIP')}</Typography>
                        </Paper>
                        <Typography align="center" style={{ lineHeight: theming.spacing(5) }}>
                            {translate('OPTIONAL_CUSTOM_INDICATOR_ADDING_TIP')}
                            <br />
                            <label htmlFor="upload-custom-indicator-file-input">
                                <Button
                                    component="span"
                                    startIcon={<CloudUpload />}
                                    style={{
                                        color: '#FFF',
                                        backgroundColor: theming.palette.secondary.main,
                                    }}
                                    variant="contained"
                                >
                                    {translate('SELECT_FILE')}
                                </Button>
                            </label>
                        </Typography>
                    </TabPanel>
                    {uploadedIndicators.map((indicator, i) => (
                        <TabPanel key={i} value={i.toString()}>
                            <ShvkTextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={indicator.name}
                                sx={{ mb: 2 }}
                                onChange={(e) => handleIndicatorNameChange(e, indicator)}
                            />
                            {indicator.data.some(
                                (dataVariable) => !isIndicatorOrganizationInCompareSettings(dataVariable.organization),
                            ) && (
                                <Typography variant="body2" className={classes.warningText}>
                                    {translate('ORGANIZATIONS_NOT_IN_COMPARISON_SETTINGS')}
                                </Typography>
                            )}
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={classes.tableCell}>{translate('ORGANIZATION')}</TableCell>
                                        {[...new Set(indicator.data.map((dataVariable) => dataVariable.year))].map(
                                            (year) => {
                                                return (
                                                    <TableCell className={classes.tableCell} key={year}>
                                                        {year}
                                                    </TableCell>
                                                );
                                            },
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {context.indicator
                                        .groupIndicatorDataByOrganization(indicator.data)
                                        .map((orgIndicatorData) => (
                                            <TableRow
                                                key={`${
                                                    orgIndicatorData.organization.name[localization.locale] ||
                                                    orgIndicatorData.organization.name.fi_FI ||
                                                    orgIndicatorData.organization.name.sv_SE ||
                                                    orgIndicatorData.organization.name.en_GB
                                                } - row`}
                                                className={`${
                                                    !isIndicatorOrganizationInCompareSettings(
                                                        orgIndicatorData.organization,
                                                    )
                                                        ? classes.warningCell
                                                        : null
                                                }`}
                                            >
                                                <TableCell
                                                    className={classes.tableCell}
                                                    key={orgIndicatorData.organization.name.id}
                                                >
                                                    {orgIndicatorData.organization.name[localization.locale] ||
                                                        orgIndicatorData.organization.name.fi_FI ||
                                                        orgIndicatorData.organization.name.sv_SE ||
                                                        orgIndicatorData.organization.name.en_GB}
                                                </TableCell>
                                                {orgIndicatorData.data.map((data) => (
                                                    <TableCell
                                                        className={classes.tableCell}
                                                        key={`${
                                                            orgIndicatorData.organization.name[localization.locale] ||
                                                            orgIndicatorData.organization.name.fi_FI ||
                                                            orgIndicatorData.organization.name.sv_SE ||
                                                            orgIndicatorData.organization.name.en_GB
                                                        } - ${data.year}`}
                                                    >
                                                        <ShvkTextField
                                                            type="number"
                                                            size="small"
                                                            fullWidth
                                                            defaultValue={data.value}
                                                            onChange={(e) =>
                                                                handleDataChange(
                                                                    e,
                                                                    indicator,
                                                                    orgIndicatorData,
                                                                    data.year,
                                                                )
                                                            }
                                                        />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TabPanel>
                    ))}
                </TabContext>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={props.close}>
                    {translate('CLOSE')}
                </Button>
                <Box flexGrow={1} />
                <ShvkButton startIcon={<Save />} onClick={save} disabled={!uploadedIndicators}>
                    {translate('SAVE')}
                </ShvkButton>
            </DialogActions>
        </StyledDialog>
    );
}

export default observer(UploadCustomIndicatorDialog);
